import cx from 'classnames'
import { Icon } from '@/components/Icon'
import { Tooltip } from '@/components/Tooltip'
import { LegendPill } from '@/features/legends/components/LegendPill'
import type { LegendStatus } from '@/features/legends/legend-status'
import { match } from '@/utils/pattern-match-util'
import styles from './LegendStatus.module.css'

export interface LegendStatusLabelProps {
  status: LegendStatus
  /**
   * Shows that a draft sits on top of the published version.
   */
  hasUnpublishedChanges?: boolean
}

/**
 * A legend's status: Draft, Published or Inactive. A published legend with a
 * newer draft shows both.
 */
export function LegendStatusLabel({
  status,
  hasUnpublishedChanges,
}: LegendStatusLabelProps) {
  const showsDraft = hasUnpublishedChanges && status !== 'Inactive'
  return (
    <Tooltip
      content={
        showsDraft
          ? 'This legend is visible to clients, and a new version is being drafted.'
          : match(status, {
              Draft:
                'This legend has an initial draft, but is not visible to clients.',
              Published:
                'This legend is visible to clients and has no changes since it was published.',
              Previous: 'This is an older version.',
              Inactive:
                'This legend is not active and is invisible to clients.',
            })
      }
    >
      <span className={styles.label}>
        <LegendStatusSegment status={status} />
        {showsDraft && <LegendStatusSegment status="Draft" />}
      </span>
    </Tooltip>
  )
}

function LegendStatusSegment({ status }: { status: LegendStatus }) {
  return (
    <LegendPill className={cx(styles.segment, getStatusClassName(status))}>
      <LegendStatusIcon status={status} />
      {match(status, {
        Draft: 'Draft',
        Published: 'Published',
        Previous: 'Published',
        Inactive: 'Inactive',
      })}
    </LegendPill>
  )
}

export interface LegendStatusIconProps {
  status: LegendStatus
}

/**
 * The status's icon, in its color.
 */
export function LegendStatusIcon({ status }: LegendStatusIconProps) {
  return (
    <Icon
      name={match(status, {
        Draft: 'legend-status-draft',
        Published: 'legend-status-published',
        Previous: 'legend-status-published',
        Inactive: 'legend-status-inactive',
      })}
      size={10}
      className={cx(styles.icon, getStatusClassName(status))}
      aria-hidden="true"
    />
  )
}

export interface VersionChipProps {
  version: number
  status: LegendStatus
  /**
   * Marks the latest draft.
   */
  isDraft?: boolean
  /**
   * Tints the chip instead of filling it.
   */
  isSubtle?: boolean
}

/**
 * The version number, colored by its status.
 */
export function VersionChip({
  version,
  status,
  isDraft,
  isSubtle,
}: VersionChipProps) {
  return (
    <LegendPill
      shape="square"
      className={cx(
        styles.chip,
        status === 'Draft' ? styles.latest : getStatusClassName(status),
        !isSubtle && styles.filled,
      )}
    >
      V{version}
      {isDraft && <span className={styles.draftDot} />}
    </LegendPill>
  )
}

function getStatusClassName(status: LegendStatus) {
  return match(status, {
    Draft: styles.draft,
    Published: styles.published,
    Previous: styles.inactive,
    Inactive: styles.inactive,
  })
}
